import { Builder } from '@builder.io/react';
import { builder } from '@builder.io/react';
import React, { useEffect, useState } from 'react';
import sanitizeHtml from 'sanitize-html';

import { Text } from '../../storyblocks-design-system/components/atoms/Text/Text';

const fetchContent = async (urlSlug: string, modelName: string) => {
  // Fetch the content, otherwise fetch the default content
  const builderOptions = urlSlug
    ? {
        query: {
          'data.urlSlug': urlSlug,
        },
        options: {
          fields: 'data',
        },
      }
    : {};

  try {
    const builderApiResponse = await builder.get(modelName, builderOptions);
    return (
      builderApiResponse?.data || {
        richTextHeader: '',
        richTextContent: '',
      }
    );
  } catch (e) {
    return {
      richTextHeader: '',
      richTextContent: '',
    };
  }
};

const RichText = ({
  urlSlug,
  modelName = 'category-search-page',
  altHeader,
  altContent,
  header,
  content,
}: {
  urlSlug?: string;
  modelName?: string;
  altHeader?: string;
  altContent?: string;
  header?: string;
  content?: string;
}) => {
  const [richTextHeader, setRichTextHeader] = useState('');
  const [richTextContent, setRichTextContent] = useState('');

  useEffect(() => {
    // If no urlSlug is provided, use the header and content directly
    if (!urlSlug && header && content) {
      setRichTextHeader(header);
      setRichTextContent(content);
      return;
    }
  }, [urlSlug, header, content]);

  useEffect(() => {
    let isMounted = true;
    if (urlSlug) {
      // If urlSlug is provided, fetch the content
      const getContent = async () => {
        const { richTextHeader, richTextContent } = await fetchContent(
          urlSlug,
          modelName
        );
        if (isMounted) {
          // If fetch is empty, use fallback content
          setRichTextHeader(richTextHeader || altHeader);
          setRichTextContent(richTextContent || altContent);
        }
      };
      getContent();
    }
    return () => {
      isMounted = false;
    };
  }, [urlSlug, modelName]);

  if (!richTextContent && !richTextHeader) return null;

  // Leaving a very strict sanitize config here to prevent XSS attacks and weird HTML
  const sanitizeConfig: sanitizeHtml.IOptions = {
    allowedTags: ['b', 'i', 'em', 'strong', 'a'],
    allowedAttributes: {
      a: ['href', 'target', 'rel'],
    },
    allowedSchemes: ['http', 'https', 'mailto'],
  };

  const sanitizedRichTextContent = sanitizeHtml(
    richTextContent,
    sanitizeConfig
  );
  const sanitizedRichTextHeader = sanitizeHtml(richTextHeader, sanitizeConfig);

  return (
    <div className="flex flex-col gap-2 items-center justify-center max-w-[483px] mx-auto">
      {richTextHeader && (
        <Text
          fontFamily="inter"
          fontWeight="semibold"
          fontSize="3xl"
          className="text-neutral-900 text-center text-3xl"
          asChild
        >
          <h1 dangerouslySetInnerHTML={{ __html: sanitizedRichTextHeader }} />
        </Text>
      )}
      {richTextContent && (
        <Text
          fontFamily="inter"
          fontSize="base"
          fontWeight="regular"
          className="text-neutral-900 text-center text-base mt-0"
          asChild
        >
          <h2 dangerouslySetInnerHTML={{ __html: sanitizedRichTextContent }} />
        </Text>
      )}
    </div>
  );
};

export default RichText;

Builder.registerComponent(RichText, {
  name: 'RichText SB',
  inputs: [],
});
